import {
	Button,
	Checkbox,
	Col,
	ConfigProvider,
	Form,
	Input,
	Row,
	DatePicker,
	Typography,
	Space,
	Select
} from 'antd'
import React, { useRef } from 'react'
import { GeneralTitle } from '../../../components/Shared'
import { observer } from 'mobx-react-lite'
import { GeneralContentWrapper } from '../../../components/Shared/GeneralComponents'
import { v4 as uuidv4 } from 'uuid'
import { actApplyForMeeting } from '../../../stores/users/actions'
import { useSmartTranslation } from '../../../cls/TranslationContext'
const { Paragraph } = Typography

const timeOptions = [
	{
		key: 'time-09:00-am',
		label: '09:00 AM',
		value: '09:00 AM'
	},
	{
		key: 'time-09:30-am',
		label: '09:30 AM',
		value: '09:30 AM'
	}
]

/**
 * @function MeetingsSetter
 * @description React component that renders a form allowing users to schedule a meeting by providing contact information, date, hour, and optional message.
 * It submits the data via `actApplyForMeeting`, displays success/failure messages, and resets the form upon successful submission.
 * @returns {JSX.Element}
 */
const MeetingsSetter = () => {
	const { t } = useSmartTranslation()
	const [form] = Form.useForm()
	const messageRef = useRef(null)
	const meeting_id = uuidv4()

	const _initialFormValues = {
		name: '',
		email: '',
		phone: '',
		date: null,
		available_hour: '',
		message: ''
	}

	/**
	 * @function _doSave
	 * @description Handles form submission logic. Sends form data to the backend using `actApplyForMeeting`. Displays feedback messages based on the API response and resets the form on success.
	 * @param {Object} vals - Form values submitted by the user.
	 */
	const _doSave = (vals) => {
		actApplyForMeeting(meeting_id, vals)
			.then((response) => {
				console.log('Clean response:', response.data)
				if (response.data?.status === 'success') {
					// Success case
					messageRef.current.style.color = 'green'
					messageRef.current.textContent = t('_meeting.scheduled-successfully')
					form.resetFields()
				} else {
					// API returned error
					messageRef.current.style.color = 'red'
					messageRef.current.textContent =
						response.data?.message || t('_meeting.scheduled-fail')
				}
			})
			.catch((error) => {
				console.error('API Error:', error)
				messageRef.current.style.color = 'red'
				messageRef.current.textContent =
					error.response?.data?.message ||
					error.message ||
					t('single.network-error')
			})
	}

	return (
		<GeneralContentWrapper>
			<GeneralTitle text={t('__schedule%_meeting')} />
			<ConfigProvider>
				<Form
					layout="vertical"
					form={form}
					name="meeting-setter"
					initialValues={_initialFormValues}
					onFinish={_doSave}
				>
					<Row
						gutter={[50, 50]}
						justify="space-between"
						align="middle"
					>
						<Col xs={24} sm={24} md={17} lg={17}>
							<Space
								style={{ width: '100%' }}
								direction="vertical"
							>
								<Paragraph>
									{t('__schedule%_meeting')}
								</Paragraph>

								<div>
									<Form.Item
										label={t('single.name')}
										tooltip={t('__enter.please%single.name')}
										name="name"
										rules={[
											{
												required: true,
												message: 'Required'
											}
										]}
									>
										<Input
											placeholder={t(
												'__provide.please%single.name'
											)}
										/>
									</Form.Item>

									<Row gutter={[20, 20]}>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label="Email"
												name="email"
												tooltip={t(
													'single.reach.email'
												)}
												rules={[
													{
														required: true,
														message: 'Required'
													},
													{
														type: 'email',
														message: t(
															'single.invalid.email'
														)
													}
												]}
											>
												<Input
													placeholder={t(
														'__provide.please%_email.address'
													)}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label={t('single.phone')}
												tooltip={t(
													'single.invalid.phone'
												)}
												name="phone"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.phone-number'
														)
													}
												]}
											>
												<Input
													placeholder={t(
														'__provide.please%single.phone-number'
													)}
												/>
											</Form.Item>
										</Col>
									</Row>

									<Row gutter={[20, 20]}>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label={t('single.date')}
												name="date"
												rules={[
													{
														required: true,
														message:
															t(
																'single.required'
															)
													}
												]}
											>
												<DatePicker
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label={t('_hour')}
												name="available_hour"
												rules={[
													{
														required: true,
														message:
															t(
																'single.required'
															)
													}
												]}
											>
												<Select
													placeholder={t(
														'__select.item%single.time'
													)}
													options={timeOptions}
												/>
											</Form.Item>
										</Col>
									</Row>

									<Form.Item
										label={t('_note.multiple')}
										name="message"
									>
										<Input.TextArea
											placeholder={t('_note.multiple')}
											rows={4}
										/>
									</Form.Item>

									<Form.Item
										name="consent"
										valuePropName="checked"
										rules={[
											{
												validator: (_, value) =>
													value
														? Promise.resolve()
														: Promise.reject(
																new Error(
																	t(
																		'single.agree.terms-and-conditions'
																	)
																)
														  )
											}
										]}
									>
										<Checkbox>
											{t('single.agree.read-and-agree')}
										</Checkbox>
									</Form.Item>

									<Form.Item style={{ marginBottom: 0 }}>
										<Button
											type="primary"
											htmlType="submit"
										>
											{t('single.submit')}
										</Button>
										<span
											ref={messageRef}
											style={{ marginLeft: 16 }}
										></span>
									</Form.Item>
								</div>
							</Space>
						</Col>

						<Col xs={24} sm={24} md={7} lg={7}>
							<img
								width="100%"
								src={`${process.env.PUBLIC_URL}/webackit-logo-color.svg`}
								alt="logo"
							/>
						</Col>
					</Row>
				</Form>
			</ConfigProvider>
		</GeneralContentWrapper>
	)
}

export default observer(MeetingsSetter)
